import { routing } from '@/src/i18n/routing';
import type { SupportedLocale } from '@/src/i18n/routing';
import { getPathname } from '@/src/i18n/navigation';
import { isSafeRedirect } from './safe-redirect';

const HOTPADS_ORIGIN = 'https://hotpads.com';

function withoutLocalePrefix(pathname: string): string {
  for (const locale of routing.locales) {
    const prefix = `/${locale}`;
    if (pathname === prefix) return '/';
    if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  }
  return pathname;
}

export function sanitizePostLoginRedirect(value: unknown): string | null {
  if (typeof value !== 'string' || !isSafeRedirect(value)) return null;

  const redirectUrl = new URL(value, HOTPADS_ORIGIN);
  if (redirectUrl.origin !== HOTPADS_ORIGIN) return null;

  const redirect = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
  if (!isSafeRedirect(redirect)) return null;

  const pathname = withoutLocalePrefix(redirectUrl.pathname);
  if (pathname === '/login' || pathname.startsWith('/login/')) return null;

  return redirect;
}

export function resolvePostLoginRedirect(
  candidates: ReadonlyArray<unknown>,
  fallback = '/',
): string {
  for (const candidate of candidates) {
    const redirect = sanitizePostLoginRedirect(candidate);
    if (redirect) return redirect;
  }

  return sanitizePostLoginRedirect(fallback) ?? '/';
}

export function getPostLoginRedirectLocale(redirect: string): SupportedLocale | null {
  const safeRedirect = sanitizePostLoginRedirect(redirect);
  if (!safeRedirect) return null;

  const pathname = new URL(safeRedirect, HOTPADS_ORIGIN).pathname;
  return (
    routing.locales.find((candidate) => {
      const prefix = `/${candidate}`;
      return pathname === prefix || pathname.startsWith(`${prefix}/`);
    }) ?? null
  );
}

export function localizePostLoginRedirect(redirect: string, locale: SupportedLocale): string {
  const safeRedirect = sanitizePostLoginRedirect(redirect) ?? '/';

  return getPostLoginRedirectLocale(safeRedirect) ? safeRedirect : getPathname({ href: safeRedirect, locale });
}
